import React, {useEffect, useState} from "react";
import PackageTrackingItemMulti from "./PackageTrackingItemMulti";
import {fromJS, List, Set} from "immutable";
import gql from "graphql-tag";
import {graphql} from "react-apollo";
import Loading from "../../components/Loading";
import GraphQLErrors from "../../components/GraphQLErrors";
import styles from "./ShipmentPackagesTrackingList.module.scss";
import {compose} from "redux";
import Button from "../../components/Button";
import {getTrackingNrField, hasShippingLabels} from "../../lib/Couriers";
import ShippingLabelStorage from "../../lib/ShippingLabelStorage";


// This loads tracking information per package and not consignment.
const ShipmentPackagesTrackingList = (props) => {
  const {trackingItemKey} = props;
  const {loading, error} = props.data;
  // Selectable logic
  const [allKeys, setAllKeys] = useState(Set());
  const [checkedKeys, setCheckedKeys] = useState(Set());

  const toggleCheckedKey = (key) => {
    if (checkedKeys.has(key)) {
      setCheckedKeys(checkedKeys.delete(key));
    } else {
      setCheckedKeys(checkedKeys.add(key));
    }
  }

  function toggleOn(keys) {
    setCheckedKeys(checkedKeys.union(keys));
  }

  function toggleOff(keys) {
    setCheckedKeys(checkedKeys.subtract(keys));
  }

  function onDownloadShippingLabel(trackingNr) {
    let downloadUrl = ShippingLabelStorage.getUrl(props.orderId, trackingNr);
    ShippingLabelStorage.download(downloadUrl);
  }

  const trackingNrField = getTrackingNrField(props.courierId);

  const [trackingWithPackages, setTrackingWithPackages] = useState(List());
  useEffect(() => {
    if (props.data.shipmentTracking || props.data.shipmentPackagesTracking) {
      const trackingPackages = getPackages(props);
      // Tracking is returned per package, so several entries can share the
      // same tracking number. Each entry already collects all packages with
      // that tracking number below, so keep only one entry per tracking number
      // to avoid rendering duplicate cards.
      const uniqueTrackingPackages = trackingPackages
        .groupBy((t) => t.get(trackingNrField))
        .map((group) => group.first())
        .toList();
      const withPackages = uniqueTrackingPackages.map((t) => {
        const trackingNrValue = t.get(trackingNrField);
        // Get all packages with this tracking number
        const packagesWithTrackingNr = props.packages.filter((p) => p.get("trackingNr") === trackingNrValue);
        // UPS packages don't have a package id, so we reconcialiate that info
        const packageId = packagesWithTrackingNr.first().get("packageId");

        return t.set("packages", packagesWithTrackingNr)
          .set("useShipmentTracking", props.useShipmentTracking)
          // "packageId" is the default tracking key and must be set
          // .set(trackingItemKey, packageId)
          // For UPS there is trackingId and for the rest trackingNr. Make it all the same
          .set("trackingNr", trackingNrValue)

      });

      setTrackingWithPackages(withPackages);
      setAllKeys(withPackages.map((p) => p.get(trackingItemKey)))
    }
  }, [trackingNrField, props.data.shipmentTracking, props.data.shipmentPackagesTracking])

  if (loading) return <Loading />
  if (error) return <GraphQLErrors error={error} />

  const packages = getPackages(props);

  if (packages.size === 0) {
    return <p>Not available</p>;
  }

  const showRetryTracking = shouldShowRetryTracking(props);

  function handleRetry() {
    props.retryTracking(props.orderId);
  }

  function handleReplacePackages() {
    props.onReplacePackages(checkedKeys.toJS());
  }

  function getReplacedPackageIdsForTrackingItem(packages, replacedPackageIds) {
    let packageIds = packages.map(p => p.get("packageId")).toSet();
    return replacedPackageIds.filter((r) => packageIds.has(r.get("packageId")));
  }

  let elements = trackingWithPackages.map((data) => {
      return <PackageTrackingItemMulti 
              key={data.get("trackingNr")} 
              data={data} 
              replacedPackageIds={getReplacedPackageIdsForTrackingItem(data.get("packages"), props.replacedPackageIds)} 
              courierId={props.courierId}
              hasShippingLabels={hasShippingLabels(props.courierId)}
              useShipmentTracking={props.useShipmentTracking}
              isChecked={(key) => checkedKeys.has(key)}
              areChecked={(keys) => keys.every((k) => checkedKeys.has(k))}
              toggleOn={toggleOn}
              toggleOff={toggleOff}
              onToggleChecked={(key) => toggleCheckedKey(key)}
              onContextMenuClick={props.onItemContextMenuClick}
              onDownloadShippingLabel={onDownloadShippingLabel}
              />
    });

  return (
    <div>
      {elements}
      <div className={styles.buttonbar}>
        <Button label="Retry Tracking"
          visible={showRetryTracking}
          onClick={handleRetry}
        />
        <div className={styles.spacer} />
        <Button label="Replace packages"
          visible={checkedKeys.size > 0}
          onClick={handleReplacePackages}
          />
      </div>
    </div>
  );
}

function getPackages(props) {
  if (props.useShipmentTracking) {
    // UPS
    const data = fromJS(props.data.shipmentTracking);
    if (data.get("packages") == null) {
      return List([]);
    }
    const packages = data.get("packages", List()).map(pack => {
      return pack.set("tracking", data.get("tracking"));
    })

    return packages;
  } else {
    // Other courriers
    return fromJS(props.data.shipmentPackagesTracking || []);
  }
}

function shouldShowRetryTracking(props) {
  return props.useShipmentTracking
    && List(["suspended", "stopped"]).includes(props.data.shipmentTracking.tracking);
}

const PACKAGES_TRACKING_QUERY = gql`
  query shipmentPackagesTracking($orderId:ID!, $courierId:String) {
    shipmentPackagesTracking(orderId: $orderId, courierId: $courierId) {
      orderId
      packageId
      trackingNr
      trackingUrl
      status
      tracking
      pickupDate
      deliveryDate
      voidDate
      updatedAt
      history {
        location
        description
        status
        date
        time
      }
    }
  }
`;

const SHIPMENT_RETRY_TRACKING = gql`
  mutation retryShipmentTracking($orderId: ID!) {
    retryShipmentTracking(orderId: $orderId) {
      errors { key message }
    }
  }
`;

const SHIPMENT_TRACKING_QUERY = gql`
  query shipmentTracking($orderId:ID!) {
    shipmentTracking(orderId: $orderId) {
      tracking
      packages {
        trackingId
        status
        pickupDate
        deliveryDate
        voidDate
        activity {
          location
          description
          status
          date
          time
        }
      }
    }
  }
`;

const withQuery = compose(
  graphql(PACKAGES_TRACKING_QUERY, {
    skip: (props) => props.useShipmentTracking,
    options: (props) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        orderId: props.orderId,
        courierId: props.courierId
      }
    })
  }),
  graphql(SHIPMENT_TRACKING_QUERY, {
    skip: (props) => !props.useShipmentTracking,
    options: (props) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        orderId: props.orderId
      }
    })
  }),
  graphql(SHIPMENT_RETRY_TRACKING, {
    props: ({mutate}) => ({
      retryTracking: (orderId) => mutate({
        variables: { orderId: orderId }
      })
    })
  }),
);

export default withQuery(ShipmentPackagesTrackingList);